import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Sparkles, Coins } from "lucide-react";
import { api } from "@/lib/apiClient";
import { usePaywall } from "@/components/Paywall";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const AiConfirmContext = createContext({ confirmAi: async () => true });
export const useAiConfirm = () => useContext(AiConfirmContext);

export function AiConfirmProvider({ children }) {
  const { openPaywall } = usePaywall();
  const [state, setState] = useState(null);
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(false);

  const confirmAi = useCallback((opts = {}) => new Promise((resolve) => {
    setState({ action: opts.action || "fact_filter", count: opts.count || 1, title: opts.title, description: opts.description, resolve });
  }), []);

  useEffect(() => {
    if (!state) return;
    setLoading(true); setEst(null);
    api.post("/ai/estimate", { action: state.action, count: state.count })
      .then(({ data }) => setEst(data))
      .catch((e) => { console.error("estimate failed", e); setEst({ cost: state.count, can_afford: true, total_remaining: null }); })
      .finally(() => setLoading(false));
  }, [state]);

  const finish = (val) => { const r = state?.resolve; setState(null); setEst(null); r && r(val); };
  const canAfford = est ? est.can_afford : true;

  return (
    <AiConfirmContext.Provider value={{ confirmAi }}>
      {children}
      <Dialog open={!!state} onOpenChange={(v) => { if (!v) finish(false); }}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="ai-estimate-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
              <span className="ek-mono-label" style={{ color: "var(--ek-primary)" }}>ESTIMATED COST</span>
            </div>
            <DialogTitle className="font-head font-semibold tracking-tight">{state?.title || "Run Justice analysis"}</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              {state?.description || "Justice will analyze the selected content. You only spend credits when you confirm."}
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border p-4 space-y-2" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>This analysis is estimated to use</span>
              <span className="font-head font-bold text-lg flex items-center gap-1.5" data-testid="ai-estimate-cost"><Coins className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />{loading ? "…" : `${est?.cost ?? state?.count} KeeperCredits`}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>Available</span>
              <span className="text-sm font-medium" data-testid="ai-estimate-available" style={{ color: canAfford ? "var(--ek-secure)" : "var(--ek-error)" }}>{est?.total_remaining != null ? `${est.total_remaining} KeeperCredits` : "—"}</span>
            </div>
          </div>

          {!loading && !canAfford && (
            <p className="text-xs" style={{ color: "var(--ek-error)" }} data-testid="ai-estimate-insufficient">
              You don't have enough KeeperCredits for this analysis. Manual documenting stays free.
            </p>
          )}

          <div className="flex flex-col gap-2">
            {canAfford ? (
              <Button onClick={() => finish(true)} disabled={loading} data-testid="ai-estimate-analyze-btn" className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                {loading ? "Estimating…" : "Analyze"}
              </Button>
            ) : (
              <Button onClick={() => { finish(false); openPaywall({ feature: "Add KeeperCredits", reason: "Top up to run more Justice analyses. Purchased credits never expire.", unlock: "credits_100" }); }} data-testid="ai-estimate-buy-btn" className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                Get 100 KeeperCredits · $9.99
              </Button>
            )}
            <Button onClick={() => finish(false)} disabled={loading} variant="ghost" data-testid="ai-estimate-cancel-btn" className="w-full rounded-md" style={{ color: "var(--ek-text-muted)" }}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AiConfirmContext.Provider>
  );
}
